/**
 * Vendor Starter Guide Page - streamlined onboarding intro
 */

import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';

import { logFunnelEvent } from '../../util/analytics';
import { Page, LayoutSingleColumn } from '../../components';
import { NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './VendorStarterGuidePage.module.css';

// Host FAQ copy (owner-approved 2026-07-03). Doubles as the FAQPage
// structured data below, so the schema can never drift from the page.
const HOST_FAQ = [
  {
    q: 'Do I need to be a professional event designer?',
    a: 'No. Some of our hosts are stylists and planners, but plenty are simply people who put together one great party and kept the pieces. If your kit looks good and works, it belongs here.',
  },
  {
    q: 'What does it cost to list?',
    a: 'Nothing. Listing is free. Merrily takes a 10% host fee from each completed booking, so you only pay when you earn.',
  },
  {
    q: 'How do I get paid?',
    a: 'Payments run through the platform. Your payout starts as soon as you confirm the kit came back, and it lands in your bank within 1 to 3 business days.',
  },
  {
    q: 'What if a renter damages my kit?',
    a: 'Every booking includes a security deposit, and Merrily Damage Protection covers verified damage beyond the deposit, up to 50% of the replacement cost. You report damage with photos within 48 hours of the return and we handle it from there.',
  },
  {
    q: 'Do I have to offer delivery?',
    a: 'No. You choose pickup, delivery, or both, and you set your own delivery fee if you offer it.',
  },
  {
    q: 'How much time does hosting take?',
    a: 'Most hosts spend under an hour creating a listing. After that, it is answering booking requests, having the kit ready on the day, and checking it back in.',
  },
];

export const VendorStarterGuidePageComponent = (props) => {
  const { history, isAuthenticated } = props;

  useEffect(() => {
    logFunnelEvent('page_view_vendor_guide');
  }, []);

  // Route based on auth state:
  //   - authenticated → straight to the new-listing wizard (/l/new)
  //   - not authenticated → /signup (the existing path)
  // Previously this always pushed to /signup. Authenticated users hit
  // AuthenticationPage's "already-logged-in" guard, which redirects
  // them to LandingPage (homepage) instead of the wizard. Result: a
  // logged-in user clicking "Create Your First Listing" landed on
  // the homepage. Now they go to the listing wizard directly.
  const handleGetStartedClick = () => {
    history.push(isAuthenticated ? '/l/new' : '/signup');
  };

  const title = "List Your Party Supplies in Santa Barbara";
  const description = "Turn your party kits into income in Santa Barbara, Goleta, Carpinteria, and Montecito. List your party kits, set your price, and earn when renters in your area book them.";

  // This page's structured data is the host FAQ. The step-less HowTo stub
  // that used to live here was invalid (Google requires steps); the REAL
  // 6-step HowTo now lives on /become-a-vendor/how-it-works.
  const schema = {
    '@context': 'http://schema.org',
    '@type': 'FAQPage',
    mainEntity: HOST_FAQ.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <Page
      className={css.root}
      title={title}
      description={description}
      schema={schema}
      scrollingDisabled={false}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.main}>
          <div className={css.container}>
            
            {/* Hero + Primary CTA */}
            <div className={css.hero}>
              <h1 className={css.title}>Your event designs deserve to be rented again and again.</h1>
              <p className={css.subtitle}>
                List your party kits, set your price, and earn when renters in your area book them. Built for designers, stylists, and quality-conscious creators and hosts.
              </p>
              <button className={css.heroCta} onClick={handleGetStartedClick}>
                Create Your First Listing →
              </button>
            </div>

            {/* How it works — 3 steps */}
            <section className={css.stepsSection}>
              <div className={css.stepsGrid}>
                <div className={css.step}>
                  <div className={css.stepNumber}>1</div>
                  <h3 className={css.stepTitle}>List your kit</h3>
                  <p className={css.stepDesc}>
                    Snap photos, describe your kit, and set your price. Our team reviews each listing for design and quality before it goes live.
                  </p>
                </div>
                <div className={css.step}>
                  <div className={css.stepNumber}>2</div>
                  <h3 className={css.stepTitle}>Accept bookings</h3>
                  <p className={css.stepDesc}>
                    Review booking requests, chat with renters, and arrange pickup or delivery on your schedule.
                  </p>
                </div>
                <div className={css.step}>
                  <div className={css.stepNumber}>3</div>
                  <h3 className={css.stepTitle}>Get paid</h3>
                  <p className={css.stepDesc}>
                    Secure payments through the platform. Your payout starts the
                    moment you confirm the kit's return, and it lands in your
                    bank within 1 to 3 business days.
                  </p>
                </div>
              </div>
            </section>

            {/* The money, plainly (audit item 17: economics were hidden
                end-to-end — hosts discovered the renter markup only via
                Preview). One honest panel, echoed at the wizard's
                pricing step. */}
            <section className={css.stepsSection}>
              <h2 className={css.sectionHeading}>How the money works</h2>
              <div className={css.stepsGrid}>
                <div className={css.step}>
                  <h3 className={css.stepTitle}>You keep 90%</h3>
                  <p className={css.stepDesc}>
                    You set your price. Merrily's host fee is 10%, taken
                    automatically from each payout. No invoices, no surprises.
                  </p>
                </div>
                <div className={css.step}>
                  <h3 className={css.stepTitle}>Renters see all-in prices</h3>
                  <p className={css.stepDesc}>
                    A 15% renter service fee is included in the price renters
                    see, so your $100 per day kit displays as $115 per day. It
                    funds platform costs and the damage protection that backs
                    your kit beyond the deposit.
                  </p>
                </div>
                <div className={css.step}>
                  <h3 className={css.stepTitle}>Estimate your earnings</h3>
                  <p className={css.stepDesc}>
                    Try the earnings calculator to see what your kit could
                    make in your category.
                  </p>
                  <NamedLink name="ProviderSignupPage" className={css.inlineLink}>
                    Open the calculator →
                  </NamedLink>
                </div>
              </div>
            </section>

            {/* Local SEO section (Phase 2, owner-approved copy 2026-07-03) */}
            <section className={css.stepsSection}>
              <h2 className={css.sectionHeading}>
                Rent out your party supplies in the Santa Barbara area
              </h2>
              <div className={css.proseBlock}>
                <p className={css.prose}>
                  Every weekend, someone in Santa Barbara, Goleta, Carpinteria, or Montecito is
                  planning a birthday, a baby shower, or a backyard celebration. Most of them
                  would rather rent a beautifully styled kit than buy a pile of decorations they
                  will use once.
                </p>
                <p className={css.prose}>
                  If you have themed decor, tableware, backdrops, or a knack for putting together
                  a party look, you already have what renters are searching for. List it once,
                  and it can earn for you again and again.
                </p>
                <p className={css.prose}>
                  Want the details first? Read{' '}
                  <NamedLink name="HostingGuidePage" className={css.inlineLink}>
                    how hosting works, step by step
                  </NamedLink>{' '}
                  or see{' '}
                  <NamedLink name="WhatToRentOutPage" className={css.inlineLink}>
                    what party supplies rent well
                  </NamedLink>
                  .
                </p>
              </div>
            </section>

            {/* Host FAQ (rendered from the same HOST_FAQ list the FAQPage
                schema above serializes) */}
            <section className={css.stepsSection}>
              <h2 className={css.sectionHeading}>Host questions, answered</h2>
              <div className={css.faqList}>
                {HOST_FAQ.map(item => (
                  <div key={item.q} className={css.faqItem}>
                    <h3 className={css.faqQuestion}>{item.q}</h3>
                    <p className={css.faqAnswer}>{item.a}</p>
                  </div>
                ))}
              </div>
            </section>

          </div>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

VendorStarterGuidePageComponent.propTypes = {};

const mapStateToProps = state => ({
  isAuthenticated: !!state.auth?.isAuthenticated,
});
const mapDispatchToProps = dispatch => ({});

const VendorStarterGuidePage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(VendorStarterGuidePageComponent);

export default VendorStarterGuidePage;
